import * as React from 'react';

import store from 'js/lib/coursera.store';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import { useLocation } from 'js/lib/useRouter';

import {
  getEligibleCourseraPlusMonthlySubscription,
  useGetCourseraPlusAnnualPrice,
  useGetCourseraPlusMonthlyPrice,
} from 'bundles/coursera-plus-common/utils/subscriptionUtils';
import type EnrollmentChoices from 'bundles/enroll-common/models/EnrollmentChoices';
import { useNaptime } from 'bundles/naptimejs';
import CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import ProductTypeObject from 'bundles/payments-common/common/ProductType';
import { useExperimentCurrencyCode } from 'bundles/payments-common/utils/localCurrencyExperimentUtils';
import { useGetLoggedInUserSubscriptions } from 'bundles/subscriptions-common/hooks/useGetLoggedInUserSubscriptions';
import { isStripeSubscription } from 'bundles/subscriptions-common/utils/isStripeSubscription';

type PropsFromNaptime = {
  cart?: CartsV2;
};

export const useIsFromPlusMonthlyToAnnualMarketingEmail = () => {
  const { query } = useLocation();
  const isFromPlusMonthlyToAnnualMarketingEmail = () => Boolean(query?.showMonthlyToAnnual);
  return {
    isFromPlusMonthlyToAnnualMarketingEmail,
  };
};

export const useIsEligibleForPlusMonthlyToAnnualUpgrade = (
  subscriptionsArg?: Array<SubscriptionsV1>,
  enrollmentChoices?: EnrollmentChoices
) => {
  const skipGetSubscriptions =
    enrollmentChoices?.isCourseraPlusMonthlySubscribed || (subscriptionsArg ?? []).length > 0;
  const { subscriptions, pending: loadingSubscriptions } = useGetLoggedInUserSubscriptions({
    skip: skipGetSubscriptions,
  });
  const mySubscriptions = (subscriptionsArg ?? []).length > 0 ? (subscriptionsArg as SubscriptionsV1[]) : subscriptions;
  const subscription = mySubscriptions && getEligibleCourseraPlusMonthlySubscription(mySubscriptions);

  const {
    data: { cart },
    pending: loadingCart,
  } = useNaptime<PropsFromNaptime>(() => {
    if (!subscription || !subscription.cartId) {
      return {};
    }

    return {
      cart: CartsV2.get(subscription.cartId),
    };
  }, [subscription?.cartId]);

  // The upgrade cart is a Coursera Plus (annual) subscription, so mirror the currency the new cart
  // would be created with — including the local-currency experiment override — and only allow the
  // upgrade when the existing cart's currency matches.
  const upgradeCurrencyCode = useExperimentCurrencyCode({
    productType: ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION,
    isNonSubscriptionCart: false,
  });

  if (enrollmentChoices?.isCourseraPlusMonthlySubscribed) {
    return { isEligibleForPlusMonthlyToAnnualUpgrade: true };
  }

  if (!subscription || !subscription.paymentProcessorId || cart?.currencyCode !== upgradeCurrencyCode) {
    return { isEligibleForPlusMonthlyToAnnualUpgrade: false };
  }

  // Only Stripe subscriptions are in scope for Plus monthly to annual upgrade.
  const isEligibleForPlusMonthlyToAnnualUpgrade = isStripeSubscription(subscription);

  return {
    isEligibleForPlusMonthlyToAnnualUpgrade,
    loading: Boolean(loadingSubscriptions || loadingCart),
  };
};

type UpgradeSurface = 'my_purchases' | 'XDP' | 'LIHP';
export const usePlusMonthlyToAnnualUpgradeData = (
  subscription: SubscriptionsV1,
  upgradeSurface: UpgradeSurface = 'my_purchases'
) => {
  const { isEligibleForPlusMonthlyToAnnualUpgrade } = useIsEligibleForPlusMonthlyToAnnualUpgrade([subscription]);
  let showPlusMonthlyToAnnualUpgrade = false;
  if (isEligibleForPlusMonthlyToAnnualUpgrade) {
    if (upgradeSurface === 'LIHP') {
      showPlusMonthlyToAnnualUpgrade = true;
    } else if (upgradeSurface === 'XDP') {
      showPlusMonthlyToAnnualUpgrade = true;
    } else if (upgradeSurface === 'my_purchases') {
      showPlusMonthlyToAnnualUpgrade = true;
    }
  }

  const [, productItemId] = subscription.productSku ? stringKeyToTuple(subscription.productSku) : [];
  const { courseraPlusAnnualPrice } = useGetCourseraPlusAnnualPrice(!showPlusMonthlyToAnnualUpgrade);
  const { courseraPlusMonthlyPrice } = useGetCourseraPlusMonthlyPrice({
    skip: !showPlusMonthlyToAnnualUpgrade,
    productItemId,
  });

  const annualSavingsAmount =
    courseraPlusAnnualPrice && courseraPlusMonthlyPrice
      ? 12 * courseraPlusMonthlyPrice.amount - courseraPlusAnnualPrice.amount
      : null;

  // Round down the monthly savings amount ($25.33 -> $25)
  const monthlySavingsAmount = annualSavingsAmount ? Math.floor(annualSavingsAmount / 12) : null;

  const annualSavings = annualSavingsAmount && (
    <ReactPriceDisplay
      value={annualSavingsAmount}
      currency={courseraPlusMonthlyPrice?.currencyCode}
      hideCurrencyCode={true}
    />
  );

  const monthlySavings = monthlySavingsAmount && (
    <ReactPriceDisplay
      value={monthlySavingsAmount}
      currency={courseraPlusMonthlyPrice?.currencyCode}
      hideCurrencyCode={true}
    />
  );

  return {
    showPlusMonthlyToAnnualUpgrade,
    annualSavings,
    monthlySavings,
    courseraPlusAnnualPrice,
  };
};

export const useShowMonthlyToAnnualUpgradeOnXDP = (enrollmentChoices?: EnrollmentChoices) => {
  const { isEligibleForPlusMonthlyToAnnualUpgrade } = useIsEligibleForPlusMonthlyToAnnualUpgrade([], enrollmentChoices);
  const showPlusMonthlyToAnnualUpgradeOnXDP = isEligibleForPlusMonthlyToAnnualUpgrade;
  return { showPlusMonthlyToAnnualUpgradeOnXDP, isEligibleForPlusMonthlyToAnnualUpgrade };
};

export type ConfirmationData = {
  userId: number;
  subscriptionId: string;
  nextBillingDate: string;
};

const CONFIRMATION_DATA_KEY = 'plusMonthlyToAnnualUpgradeConfirmationData';

export const saveUpgradeData = ({
  userId,
  subscriptionId,
  nextBillingDate,
}: {
  userId: number;
  subscriptionId: string;
  nextBillingDate: string;
}) => {
  const data = {
    userId,
    subscriptionId,
    nextBillingDate,
  };
  store.set(CONFIRMATION_DATA_KEY, JSON.stringify(data));
};

export const clearUpgradeData = () => {
  store.remove(CONFIRMATION_DATA_KEY);
};

export const getUpgradeData = (): ConfirmationData | undefined => {
  const rawData = store.get(CONFIRMATION_DATA_KEY);
  return rawData ? JSON.parse(rawData) : undefined;
};
